import type FileSystemItem from 'devextreme/file_management/file_system_item';
import type { MountRoute } from './createFileBrowserProvider';
import { parseItemPathForRoutes } from './createFileBrowserProvider';

export interface FileBrowserExtraFileSelection {
  mountId: string;
  path: string;
  name: string;
}

export interface FileBrowserExtraFileAction {
  name: string;
  text: string;
  icon?: string;
  accept: (fileName: string) => boolean;
  onSelect: (files: FileBrowserExtraFileSelection[]) => void;
}

export const JARVIS_ADD_TO_ANALYTICS = 'jarvisAddToAnalytics';

export function isAnalyticsSourceFileName(fileName: string): boolean {
  return /\.(csv|json|xml|xlsx|xls)$/i.test(fileName);
}

export function extraActionsForSelection(
  selected: readonly FileSystemItem[],
  extraActions: readonly FileBrowserExtraFileAction[] | undefined
): FileBrowserExtraFileAction[] {
  if (!extraActions?.length) return [];
  const files = selected.filter((item) => !item.isDirectory);
  if (files.length === 0) return [];
  return extraActions.filter((action) => files.every((item) => action.accept(item.name)));
}

export function extraToolbarItems(actions: readonly FileBrowserExtraFileAction[]) {
  if (actions.length === 0) return [];
  return [
    { name: 'separator', visible: true },
    ...actions.map((action) => ({
      name: action.name,
      widget: 'dxButton',
      visible: true,
      options: {
        icon: action.icon ?? 'chart',
        text: action.text,
        stylingMode: 'text',
        hint: action.text
      },
      showText: 'inMenu'
    }))
  ];
}

export function extraContextMenuItems(actions: readonly FileBrowserExtraFileAction[]) {
  return actions.map((action, index) => ({
    name: action.name,
    text: action.text,
    icon: action.icon ?? 'chart',
    beginGroup: index === 0
  }));
}

export function invokeExtraFileAction(
  name: string | undefined,
  selected: readonly FileSystemItem[],
  routes: readonly MountRoute[],
  extraActions: readonly FileBrowserExtraFileAction[] | undefined
): boolean {
  const action = extraActions?.find((item) => item.name === name);
  if (!action) return false;
  const files: FileBrowserExtraFileSelection[] = [];
  for (const item of selected) {
    if (item.isDirectory) continue;
    const parsed = parseItemPathForRoutes(item, routes);
    if (!parsed?.relative) continue;
    if (!action.accept(item.name)) continue;
    files.push({ mountId: parsed.mount.id, path: parsed.relative, name: item.name });
  }
  if (files.length === 0) return true;
  action.onSelect(files);
  return true;
}
